import Cookies from 'js-cookie';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import * as types from './constants';
import * as actions from './actions';
import { handleErrors, fetch } from '../../api';
import api from '../../api/endpoints';

const COOKIE_KEY = 'ai_chat_token';
const COOKIE_EXPIRES_KEY = 'ai_chat_token_expires_at';
const REFRESH_BUFFER_SECONDS = 30;

const isTokenValid = expiresAt => {
  if (!expiresAt) return false;

  const expiresMs = new Date(expiresAt).getTime();
  const nowMs = Date.now();
  const bufferMs = REFRESH_BUFFER_SECONDS * 1000;

  return expiresMs - nowMs > bufferMs;
};

// `onSuccess` gets the token in use after the request (cached or fetched), so a caller can await it.
function* getAiChatToken({ payload: { force, onSuccess, onError } = {} }) {
  // Check cached state first (Redux).
  const { token, expiresAt } = yield select(state => state.aiChatToken);

  if (!force && token && isTokenValid(expiresAt)) {
    // Settles the request's `loading` without changing the token.
    yield put(actions.getAiChatTokenDone({ token, expires_at: expiresAt }));
    onSuccess && onSuccess(token);
    return;
  }

  // Fallback: check cookies (survives page reload).
  if (!force) {
    const cookieToken = Cookies.get(COOKIE_KEY);
    const cookieExpiresAt = Cookies.get(COOKIE_EXPIRES_KEY);

    if (cookieToken && isTokenValid(cookieExpiresAt)) {
      yield put(actions.getAiChatTokenDone({ token: cookieToken, expires_at: cookieExpiresAt }));
      onSuccess && onSuccess(cookieToken);
      return;
    }
  }

  try {
    const { data } = yield call(fetch, { api: api.ai_chat_token });
    const result = data.result;

    // if (!result.token && ENVIRONMENT !== 'production') {
    //   const { api_token } = yield select(state => state.authData);
    //   result.token = api_token;
    // }

    // Persist to cookies so the token survives page reloads.
    const expiryDate = new Date(result.expires_at);
    Cookies.set(COOKIE_KEY, result.token, { expires: expiryDate, sameSite: 'Strict' });
    Cookies.set(COOKIE_EXPIRES_KEY, result.expires_at, { expires: expiryDate, sameSite: 'Strict' });

    yield put(actions.getAiChatTokenDone(result));
    onSuccess && onSuccess(result.token);
  } catch (error) {
    yield handleErrors({
      error,
      putErrorData: actions.getAiChatTokenFailed,
      endpoint: api.ai_chat_token
    });
    // Called here rather than through handleErrors, which skips it when the request got no response.
    onError && onError(error);
  }
}

// eslint-disable-next-line require-yield
function* clearAiChatToken() {
  Cookies.remove(COOKIE_KEY);
  Cookies.remove(COOKIE_EXPIRES_KEY);
}

export const getAiChatTokenSaga = takeEvery(types.GET_AI_CHAT_TOKEN_REQUEST, getAiChatToken);
export const clearAiChatTokenSaga = takeEvery(types.CLEAR_AI_CHAT_TOKEN, clearAiChatToken);
